import React from 'react';
import { size, uniqBy } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Table, Icon, IconButton, Tooltip, Whisper } from 'rsuite';
import { toastr } from 'react-redux-toastr';
import {
  reservationsROPicklistEditPath,
  reservationsROPicklistViewPath
} from '../../../../routes/Paths';
import pagePathToUrl from '../../../../helpers/pagePathToUrl';
import * as PERMISSIONS from '../../../../constants/permissions/reservation';
import { modifyAccess, fullAccess } from '../../../../helpers/permission';
import IconLink from '../../../IconLink';
import AccessControl from '../../../AccessControl';
import history from '../../../../routes/history';

const { Cell } = Table;

const ActionCell = ({
  rowData,
  dataKey,
  onClone,
  onDelete,
  isCloning,
  isDeleting,
  ...props
}) => {
  const { id } = rowData;

  const handleClone = () => {
    const { items, categories } = rowData;
    const hasEmptyItems =
      size(uniqBy('categoryId', items)) !== size(categories);

    if (hasEmptyItems) {
      toastr.error(
        'Error',
        'Unable to Clone Pick List. Please fix form error first.'
      );
      history.push(pagePathToUrl(reservationsROPicklistEditPath, { id }));
    } else {
      onClone(rowData);
    }
  };

  return (
    <Cell {...props} className="link-group">
      <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.RO_ITEM)}>
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>Edit</Tooltip>}
        >
          <IconLink
            icon="edit2"
            to={pagePathToUrl(reservationsROPicklistEditPath, { id })}
          />
        </Whisper>
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>Clone</Tooltip>}
        >
          <IconButton
            appearance="link"
            style={{ top: '-8px' }}
            icon={<Icon icon="clone" />}
            disabled={isCloning}
            onClick={handleClone}
          />
        </Whisper>
      </AccessControl>

      <Whisper
        placement="top"
        trigger="hover"
        speaker={<Tooltip>View</Tooltip>}
      >
        <IconLink
          icon="eye"
          to={pagePathToUrl(reservationsROPicklistViewPath, { id })}
        />
      </Whisper>

      <AccessControl allowOnlyFor={fullAccess(PERMISSIONS.RO_ITEM)}>
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>Delete</Tooltip>}
        >
          <IconButton
            style={{ top: '-8px' }}
            appearance="link"
            color="red"
            onClick={() => {
              onDelete(rowData);
            }}
            disabled={isDeleting}
            icon={<Icon icon="trash" />}
          />
        </Whisper>
      </AccessControl>
    </Cell>
  );
};

ActionCell.propTypes = {
  rowData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    trackingMethod: PropTypes.shape({}).isRequired,
    description: PropTypes.string,
    notes: PropTypes.string,
    limitRules: PropTypes.arrayOf(PropTypes.shape({})),
    items: PropTypes.arrayOf(PropTypes.shape({})),
    categories: PropTypes.arrayOf(PropTypes.shape({})),
    isVisible: PropTypes.bool.isRequired,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string
  }),
  dataKey: PropTypes.string.isRequired,
  onClone: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isCloning: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired
};

ActionCell.defaultProps = {
  rowData: null
};

export default ActionCell;
